import { useEffect, useState } from "react";
import { IconDownload, IconX } from "@tabler/icons-react";
import FileUploader from "../../../components/common/FileUploader";
import toast from "react-hot-toast";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useLanguage } from "../../../context/LanguageContext";
 
type StaffBulkResponseModalsProps = {
  onClose: () => void;
  data: {
    successful: any[]; // ideally type with CSVStaffData[]
    failed: any[];
  };
};

export default function MerchantBulkResponseModals({
  onClose,
  data,
}: StaffBulkResponseModalsProps) {
  const { t } = useLanguage();
  const [file, setFile] = useState<File | null>(null);

  console.log("BulkResponsedatadatadatadatadata", data);

  const handleDownloadFailedCSV = async () => {
    if (data && data.failed && data.failed.length > 0) {
      try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Failed Staffs");

        // Collect all unique keys except "reason"
        const allKeys = Array.from(
          new Set(data.failed.flatMap((item: any) => Object.keys(item || {})))
        ).filter((key) => key !== "reason");

        // Add "reason" as last column
        const headings = [...allKeys, "reason"];

        // Row 1: Info row
        const infoRow = worksheet.addRow([
          "This file contains failed Merchant import records. Please fix the red highlighted issues and re-upload.",
        ]);
        worksheet.mergeCells(
          `A1:${String.fromCharCode(65 + headings.length - 1)}1`
        );
        infoRow.eachCell((cell: any) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "E2EFD9" },
          };
          cell.font = {
            name: "Arial",
            size: 10,
            bold: true,
            color: { argb: "212121" },
          };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          cell.border = {
            top: { style: "thin", color: { argb: "000000" } },
            left: { style: "thin", color: { argb: "000000" } },
            bottom: { style: "thin", color: { argb: "000000" } },
            right: { style: "thin", color: { argb: "000000" } },
          };
        });
        infoRow.height = 25;

        // Row 2: Validation row
        const validationRow = worksheet.addRow([
          "Full name is required",
          "Phone (10–12 digits) is required",
          "Valid email is required",
          "Contact Person Name required",
          "Contact Method (Phone, Email & Whatsapp)",
          "Warehouse is required",
          "Payment Term (Daily, Weekly & Monthly)",
          "Access Type (LM, FM & Both)",
          "Merchant Type is required",
          "Short Address is required",
          "Full Address is required",
          "Vat Number (15 digits) is required",
          "CR Number (10 digits) is required",
          "National Id (10 digits) is required",
          "Contract Date (YYYY-MM-DD) is required",
          "Reason not required",
        ]);
        validationRow.eachCell((cell: any, col: number) => {
          let fillColor = col === headings.length ? "FFEB9C" : "FFCCCC"; // yellow for reason col, red for others
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: fillColor },
          };
          cell.font = {
            name: "Arial",
            size: 10,
            bold: true,
            color: { argb: "212121" },
          };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          cell.border = {
            top: { style: "thin", color: { argb: "000000" } },
            left: { style: "thin", color: { argb: "000000" } },
            bottom: { style: "thin", color: { argb: "000000" } },
            right: { style: "thin", color: { argb: "000000" } },
          };
        });
        validationRow.height = 50;

        // Row 3: Headings
        const displayHeadings = headings.map((h: string) => h === "location" ? "full_address" : h);
        const headingRow = worksheet.addRow(displayHeadings);
        headingRow.eachCell((cell: any) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "217447" },
          };
          cell.font = {
            name: "Arial",
            size: 11,
            bold: true,
            color: { argb: "FFFFFF" },
          };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          cell.border = {
            top: { style: "thin", color: { argb: "000000" } },
            left: { style: "thin", color: { argb: "000000" } },
            bottom: { style: "thin", color: { argb: "000000" } },
            right: { style: "thin", color: { argb: "000000" } },
          };
        });
        headingRow.height = 20;

        // Failed data rows
        data.failed.forEach((item: any) => {
          const rowValues = headings.map((key) => item[key] ?? "");
          const row = worksheet.addRow(rowValues);

          row.eachCell((cell: any, colNumber: number) => {
            if (headings[colNumber - 1] === "reason") {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFCCCC" },
              };
              cell.font = {
                name: "Arial",
                size: 10,
                color: { argb: "990000" },
              };
            }
            cell.alignment = {
              horizontal: "center",
              vertical: "middle",
              wrapText: true,
            };
            cell.border = {
              top: { style: "thin", color: { argb: "000000" } },
              left: { style: "thin", color: { argb: "000000" } },
              bottom: { style: "thin", color: { argb: "000000" } },
              right: { style: "thin", color: { argb: "000000" } },
            };
          });
        });

        // Auto column widths
        worksheet.columns = headings.map(() => ({ width: 25 }));

        // Export Excel
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), "failed-merchant.xlsx");

        toast.success(t("Failed Merchant Excel downloaded successfully"));
      } catch (err) {
        console.error("Excel generation error:", err);
        toast.error(t("Failed to generate Excel file"));
      }
    } else {
      toast.error(t("No failed data available to download"));
    }
  };

  return (
    <div className="relative bg-white rounded-3xl shadow-sm border flex flex-col overflow-hidden h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <h2 className="text-xl font-semibold text-[#212121]">
          {t("Upload Status Report")}
        </h2>

        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="p-2 rounded-lg border border-[#D2D2D2]"
          >
            <IconX size={20} />
          </button>
        </div>
      </div>

      <div className="space-y-4 p-6">
        <div
          className={` grid  ${
            data?.successful?.length > 0 && data?.failed?.length > 0
              ? "grid-cols-2 w-[800px] gap-4 "
              : "grid-cols-1 w-[650px] gap-y-4 "
          } `}
        >
          {data?.successful?.length > 0 && data?.failed?.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-0">
              <svg
                height="60"
                viewBox="0 0 520 520"
                width="60"
                xmlns="http://www.w3.org/2000/svg"
                id="fi_5290117"
              >
                <g id="_68-Contract" data-name="68-Contract">
                  <path
                    d="m80.144 30v406.177h179.195v-20l110.661-110.654h20v-275.523z"
                    fill="#d8d8d8"
                  ></path>
                  <g fill="#707070">
                    <path d="m144.45 118.951h181.277v20h-181.277z"></path>
                    <path d="m144.45 174.813h181.277v20h-181.277z"></path>
                    <path d="m144.45 230.675h181.277v20h-181.277z"></path>
                    <path d="m144.45 286.538h54.666v20h-54.666z"></path>
                  </g>
                  <path
                    d="m339.884 488a99.956 99.956 0 1 1 99.955-99.955 100.069 100.069 0 0 1 -99.955 99.955z"
                    fill="#b0ef8f"
                  ></path>
                  <path
                    d="m316.09 444.313-28.789-31.988 14.865-13.379 13.43 14.921 61.792-73.313 15.293 12.89z"
                    fill="#009045"
                  ></path>
                </g>
              </svg>

              <p className="text-xl font-medium">
                {t("Data Uploaded Successfully")}
              </p>
            </div>
          )}
          {data?.successful?.length > 0 && data?.failed?.length > 0 && (
            <div className="  gap-4 bg-green-500/10  rounded-xl border-[#D2D2D2] border max-h-[24rem] overflow-y-auto scrollbar-clean">
              <p className="text-sm text-black font-medium p-2">
                {t("Successfully Data List")} ({data?.successful?.length})
              </p>
              <table className="w-full px-4">
                <thead className="  text-[#212121]/70 border-y border-dashed border-black/10">
                  <th className="text-left font-medium p-1 px-2 text-xs">
                    #
                  </th>
                  <th className="text-left font-medium p-1 px-2 text-xs">
                    {t("Reference Number")}
                  </th>
                  <th className="text-left font-medium p-1 px-2 text-xs">
                    {t("Merchant ID")}
                  </th>
                  <th className="text-left font-medium p-1 px-2 text-xs">
                    {t("Receiver Name")}
                  </th>
                </thead>
                <tbody className="divide-y divide-black/10  ">
                  {data?.successful.map((item, index) => (
                    <tr key={index} className="text-xs font-[#212121]">
                      <tr key={index} className="text-xs font-[#212121]">
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2">{item.name}</td>
                        <td className="p-2">{item.mobile}</td>
                        <td className="p-2">{item.email}</td>
                      </tr>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data?.failed?.length > 0 && (
            <div className=" gap-4 bg-red-500/10 rounded-xl border-[#D2D2D2] border w-full max-h-[24rem] overflow-y-auto scrollbar-clean ">
              <p className="text-sm text-black font-medium  p-2">
                {t("Failed Data List")} ({data?.failed?.length})
              </p>
              <table className="w-full px-4 ">
                <thead className="  text-[#212121]/70 border-y border-dashed border-black/10">
                  <th className="text-left font-medium p-1 px-2 text-xs">
                    #
                  </th>
                  <th className="text-left font-medium p-1 px-2 text-xs">
                    {t("Name")}
                  </th>
                  <th className="text-left font-medium p-1 px-2 text-xs">
                    {t("Mobile Number")}
                  </th>
                  <th className="text-left font-medium p-1 px-2 text-xs w-72">
                    {t("Error")}
                  </th>
                </thead>
                <tbody className="divide-y divide-black/10 ">
                  {data?.failed.map((item, index) => (
                    <tr key={index} className="text-xs font-[#212121]">
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2">{item.name}</td>
                      <td className="p-2">{item.mobile}</td>
                      <td className="p-2 ">
                        <p className="line-clamp-1 ">{item.reason}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data?.failed?.length > 0 && (
            <div className="col-span-2 flex items-center justify-between gap-4 bg-[#fff] px-4 py-2 rounded-md border-[#D2D2D2] border border-black/5 ">
              <p className="text-xs text-[#212121] font-medium">
                <b>{t("Note:")}</b> {t("We found")} {data?.failed?.length} {t("records failed to upload. Review the CSV file, correct the errors, and re-upload.")}
              </p>
              <button
                onClick={handleDownloadFailedCSV}
                className="bg-[#212121] text-white flex items-center space-x-1 p-2 px-4 rounded-md text-sm flex-shrink-0"
              >
                <IconDownload size={20} />
                <span>{t("Failed Data")}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

